/**
 * Inline completion — the dimmed remainder shown behind a search field.
 *
 * Two fields use it (the projects page and the header's project switcher) and
 * their markup genuinely differs: one is a bare `<input>`, the other sits in a
 * `mat-form-field` whose padding has to be matched. The *logic* is the same in
 * both, though, and that is what lives here — a second copy of "which name
 * completes this, and which key accepts it" is exactly how the two would drift
 * apart.
 */

/**
 * The first candidate that begins with what has been typed, with the typed part
 * left untouched.
 *
 * Only prefix matches can be completed inline: the ghost has to line up
 * character for character with the input, so a hit in the middle of a name
 * would render nonsense. The caller passes candidates in its own ranked order,
 * so the best prefix match wins.
 *
 * Returns `''` when nothing completes — the caller renders no ghost then.
 */
export function inlineSuggestion(query: string, candidates: string[]): string {
  const trimmed = query.trim();
  if (!trimmed) {
    return '';
  }

  const lowered = trimmed.toLowerCase();
  const match = candidates.find((candidate) =>
    candidate.toLowerCase().startsWith(lowered),
  );

  // Keep the user's own casing on screen and append only the remainder, rather
  // than rewriting what they typed under the cursor.
  return match ? query + match.slice(query.length) : '';
}

/**
 * Whether a keystroke should accept the completion.
 *
 * `Tab` is the convention for ghost text (Gmail, fish, zsh-autosuggestions);
 * `ArrowRight` follows the browser address bar, but only with the caret already
 * at the end so it still moves inside the text otherwise. `Enter` is left to the
 * caller: on a page with nothing to submit it may accept, but in a picker it
 * should select rather than complete.
 *
 * Returns `false` when there is nothing to accept, which is what keeps `Tab`
 * moving focus instead of trapping a keyboard user in the field.
 */
export function acceptsInlineSuggestion(
  event: KeyboardEvent,
  query: string,
  suggestion: string,
): boolean {
  if (!suggestion || suggestion === query) {
    return false;
  }

  if (event.key === 'ArrowRight') {
    const input = event.target as HTMLInputElement | null;
    return input?.selectionStart === query.length;
  }

  return true;
}
